import React, { useMemo, useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { assessmentsService } from '../api/assessmentsService';
import { calculateOnboardingProgress } from '../utils/progress';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUrl';

const portalPercent = (onboardingStep?: number, onboardingCompleted?: boolean) => {
  if (onboardingCompleted) return 100;
  const step = onboardingStep || 1;
  const total = 11; // backend model validates max 11
  return Math.max(0, Math.min(100, Math.round((step / total) * 100)));
};

const stepLabels = [
  { key: 'profile', label: 'Profile' },
  { key: 'whyChurchPlanting', label: 'Why Church Planting' },
  { key: 'references', label: 'References' },
  { key: 'sendingChurch', label: 'Sending Church' },
  { key: 'background', label: 'Background Check' },
  { key: 'prepareEnrich', label: 'Prepare/Enrich' },
  { key: 'spouseInterview', label: 'Spouse Interview' },
  { key: 'theology', label: 'Theology' },
  { key: 'spiritualHealth', label: 'Spiritual Health' },
  { key: 'ministryTransitions', label: 'Ministry Transitions' },
] as const;

type StepKey = typeof stepLabels[number]['key'];

type StepState = 'complete' | 'in_progress' | 'incomplete';

const StepStatusIcon: React.FC<{ step: StepKey; state: StepState; title?: string }> = ({ step, state, title }) => {
  const color = state === 'complete' ? 'text-green-600' : state === 'in_progress' ? 'text-yellow-600' : 'text-gray-400';
  const border =
    state === 'complete'
      ? 'border-green-200 bg-green-50'
      : state === 'in_progress'
        ? 'border-yellow-200 bg-yellow-50'
        : 'border-gray-200 bg-white';

  const icon = (() => {
    switch (step) {
      case 'profile':
        // user/profile
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21a8 8 0 10-16 0" strokeLinecap="round" />
            <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'theology':
        // book
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 5.5A2.5 2.5 0 016.5 3H20v18H6.5A2.5 2.5 0 014 18.5v-13z" strokeLinejoin="round" />
            <path d="M20 21V3" strokeLinecap="round" />
          </svg>
        );
      case 'references':
        // people
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 11a3 3 0 100-6 3 3 0 000 6z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 11a3 3 0 100-6 3 3 0 000 6z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 21a6 6 0 0112 0" strokeLinecap="round" />
            <path d="M12 21a6 6 0 0110 0" strokeLinecap="round" />
          </svg>
        );
      case 'sendingChurch':
        // church/building
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v5" strokeLinecap="round" />
            <path d="M10 5h4" strokeLinecap="round" />
            <path d="M6 10l6-4 6 4v11H6V10z" strokeLinejoin="round" />
            <path d="M10 21v-6h4v6" strokeLinejoin="round" />
          </svg>
        );
      case 'prepareEnrich':
        // heart
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 21s-7-4.35-9.5-8.5C.6 9.1 2.3 6 5.8 6c1.9 0 3.1 1 3.9 2c.8-1 2-2 3.9-2c3.5 0 5.2 3.1 3.3 6.5C19 16.65 12 21 12 21z"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'spiritualHealth':
        // prayer hands-ish (simple)
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 3l-2 8 3 3-1 7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13 3l2 8-3 3 1 7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 11h6" strokeLinecap="round" />
          </svg>
        );
      case 'background':
        // shield/check
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 3l8 4v6c0 5-3.5 9-8 10c-4.5-1-8-5-8-10V7l8-4z"
              strokeLinejoin="round"
            />
            <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'whyChurchPlanting':
        // lightbulb / idea
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21h6M12 3a6 6 0 014 10.5V17H8v-3.5A6 6 0 0112 3z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'spouseInterview':
        // phone/call
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'ministryTransitions':
        // clipboard-check
        return (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="9" y="3" width="6" height="4" rx="1" strokeLinejoin="round" />
            <path d="M9 14l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      default:
        return null;
    }
  })();

  return (
    <span
      className={['inline-flex h-7 w-7 items-center justify-center rounded-md border', border, color].join(' ')}
      title={title}
      aria-label={title}
    >
      {icon}
    </span>
  );
};

const PersonCell: React.FC<{ name: string; email?: string | null; phone?: string | null }> = ({ name, email, phone }) => {
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-900">{name}</div>
      <div className="text-sm text-gray-700">{email || '—'}</div>
      <div className="text-sm text-gray-700">{phone || '—'}</div>
    </div>
  );
};

const ProgressBar: React.FC<{ value: number; barClassName: string }> = ({ value, barClassName }) => {
  const v = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-gray-200">
      <div className={['h-2 rounded-full', barClassName].join(' ')} style={{ width: `${v}%` }} />
    </div>
  );
};

const SendingChurchCell: React.FC<{
  sendingChurch?: {
    churchName?: string | null;
    primaryContactName?: string | null;
    primaryContactPhone?: string | null;
    noSendingChurch?: boolean;
    noSendingChurchExplanation?: string | null;
  } | null;
}> = ({ sendingChurch }) => {
  const [showNoSendingChurchDetails, setShowNoSendingChurchDetails] = useState(false);

  if (!sendingChurch) {
    return <span className="text-gray-400">Not provided</span>;
  }

  if (sendingChurch.noSendingChurch) {
    const reason = (sendingChurch.noSendingChurchExplanation || '').trim();
    return (
      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-900">No sending church</div>

        {reason ? (
          <>
            <button
              type="button"
              className="text-xs font-medium text-primary-700 hover:text-primary-800 hover:underline underline-offset-2"
              onClick={() => setShowNoSendingChurchDetails((v) => !v)}
              aria-expanded={showNoSendingChurchDetails}
            >
              Details
            </button>
            {showNoSendingChurchDetails ? (
              <div className="text-sm text-gray-700 whitespace-pre-wrap">{reason}</div>
            ) : null}
          </>
        ) : (
          <div className="text-sm text-gray-600">No reason provided</div>
        )}
      </div>
    );
  }

  const churchName = sendingChurch.churchName;
  const contactName = sendingChurch.primaryContactName;
  const contactPhone = sendingChurch.primaryContactPhone;
  const hasAny = !!(churchName || contactName || contactPhone);
  if (!hasAny) return <span className="text-gray-400">Not provided</span>;

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-900">{churchName || '—'}</div>
      <div className="text-sm text-gray-700">{contactName || '—'}</div>
      <div className="text-sm text-gray-700">{contactPhone || '—'}</div>
    </div>
  );
};

const AssessorCell: React.FC<{ assessorName?: string | null; assessorUser?: { firstName: string; lastName: string; email: string } | null }> = ({
  assessorName,
  assessorUser,
}) => {
  const displayName = assessorUser ? `${assessorUser.firstName} ${assessorUser.lastName}`.trim() : (assessorName || '');
  const email = assessorUser?.email || null;

  if (!displayName && !email) return <span className="text-gray-400">—</span>;

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-gray-900">{displayName || '—'}</div>
      <div className="text-sm text-gray-700">{email || '—'}</div>
    </div>
  );
};

const displayCandidateName = (c: any) =>
  c?.name || [c?.firstName, c?.lastName].filter(Boolean).join(' ') || '—';

const displaySpouseName = (c: any) =>
  c?.spouseName || [c?.wifeFirstName, c?.wifeLastName].filter(Boolean).join(' ') || 'Spouse';

const AssessmentDetail: React.FC = () => {
  const { id } = useParams();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const [activeTab, setActiveTab] = useState<'candidates' | 'day-schedule' | 'schedule' | 'assignments' | 'reports'>('candidates');
  const [activeTeamKey, setActiveTeamKey] = useState<'A' | 'B'>('A');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const hasRole = (roleName: string) => (
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false
  );
  const canSeeReports = hasRole('assessment-admin') || hasRole('assessor') || hasRole('admin') || hasRole('staff');
  const isAdmin = hasRole('admin');
  const canSeeEventDetails = hasRole('assessment-admin') || isAdmin || hasRole('staff');
  const canConfirmAttendance = hasRole('assessor') || hasRole('admin') || hasRole('staff') || hasRole('hub_leader');

  const { data, isLoading, error } = useQuery({
    queryKey: ['assessments', assessmentId],
    queryFn: () => assessmentsService.get(assessmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const matrixQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'interview-matrix'],
    queryFn: () => assessmentsService.getInterviewMatrix(assessmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const masterScheduleQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'master-schedule'],
    queryFn: () => assessmentsService.getMasterSchedule(assessmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const reportAssignmentsQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'report-assignments'],
    queryFn: () => assessmentsService.listReportAssignments(assessmentId),
    enabled: canSeeReports && Number.isFinite(assessmentId) && assessmentId > 0 && (activeTab === 'reports' || activeTab === 'schedule'),
  });

  const attendanceQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'attendance', 'me'],
    queryFn: () => assessmentsService.getMyAttendance(assessmentId),
    enabled: canConfirmAttendance && Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const [isEditingAttendance, setIsEditingAttendance] = useState(true);
  const [attendanceDraft, setAttendanceDraft] = useState({
    isAttending: true,
    hotelNeeded: false,
    hotelSundayNight: false,
    hotelMondayNight: false,
    guestCount: 0,
    guestNames: '',
    dietaryRestrictions: '',
  });

  useEffect(() => {
    if (!attendanceQuery.isLoading) {
      const existing = attendanceQuery.data?.data;
      setAttendanceDraft({
        isAttending: existing?.isAttending ?? true,
        hotelNeeded: existing?.hotelNeeded ?? false,
        hotelSundayNight: existing?.hotelSundayNight ?? false,
        hotelMondayNight: existing?.hotelMondayNight ?? false,
        guestCount: existing?.guestCount ?? 0,
        guestNames: (existing?.guestNames || []).join(', '),
        dietaryRestrictions: existing?.dietaryRestrictions || '',
      });
      setIsEditingAttendance(!existing);
    }
  }, [attendanceQuery.isLoading, attendanceQuery.data?.data?.id, attendanceQuery.data?.data?.updatedAt]);

  const attendanceMutation = useMutation({
    mutationFn: (payload: {
      isAttending: boolean;
      hotelNeeded: boolean;
      hotelSundayNight: boolean;
      hotelMondayNight: boolean;
      guestCount: number;
      guestNames: string;
      dietaryRestrictions: string;
    }) => assessmentsService.updateMyAttendance(assessmentId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'attendance', 'me'] });
      setIsEditingAttendance(false);
    },
  });

  const portalCandidateDetails = useMemo(() => {
    const participants = data?.data?.participants || [];
    return participants
      .filter((p) => p.participantType === 'portal_candidate')
      .map((p) => {
        const c = p.portalCandidate;
        const candidateName =
          c?.name ||
          [c?.firstName, c?.lastName].filter(Boolean).join(' ') ||
          '—';
        const candidateEmail = c?.user?.email || c?.email || null;
        const candidatePhone = c?.phone || null;
        const spouseName = c?.spouseName
          ? c.spouseName
          : [c?.wifeFirstName, c?.wifeLastName].filter(Boolean).join(' ');
        const spouseEmail = c?.wifeEmail || null;
        const spousePhone = c?.wifePhone || null;
        const isMarried = (c?.maritalStatus || '').toLowerCase() === 'married';
        const sermonReferenceUrl = (c as { sermonReferenceUrl?: string | null } | null | undefined)?.sermonReferenceUrl || null;

        const portalProgress = typeof (c as any)?.portalProgressPercentage === 'number'
          ? (c as any).portalProgressPercentage
          : calculateOnboardingProgress(c as any, []);

        const teamProgress = typeof (c as any)?.teamProgressPercentage === 'number'
          ? (c as any).teamProgressPercentage
          : 0;

        // Portal step icon states (candidate-owned)
        const prepareEnrichSkipped = (c as any)?.prepareEnrichData?.skipped === true;
        const spouseInterviewSkipped = (c as any)?.spouseInterviewData?.skipped === true;
        const stepState: Record<StepKey, StepState> = {
          profile: c?.profileCompleted ? 'complete' : 'incomplete',
          whyChurchPlanting: (c as any)?.whyChurchPlantingCompleted ? 'complete' : 'incomplete',
          references: c?.referencesCompleted ? 'complete' : 'incomplete',
          sendingChurch: c?.sendingChurchCompleted ? 'complete' : 'incomplete',
          background:
            c?.backgroundCheckStatus === 'submitted' || c?.backgroundCheckStatus === 'received'
              ? 'complete'
              : c?.backgroundCheckStatus === 'started'
                ? 'in_progress'
                : 'incomplete',
          prepareEnrich:
            !isMarried
              ? (prepareEnrichSkipped ? 'complete' : 'incomplete')
              : (c as any)?.prepareEnrichRequested
                ? 'complete'
                : 'incomplete',
          spouseInterview:
            !isMarried
              ? (spouseInterviewSkipped ? 'complete' : 'incomplete')
              : (c as any)?.spouseInterviewSubmitted
                ? 'complete'
                : 'incomplete',
          theology: c?.theologyCompleted ? 'complete' : 'incomplete',
          spiritualHealth:
            !isMarried
              ? (c?.spiritualHealthCandidateSubmitted ? 'complete' : 'incomplete')
              : (c?.spiritualHealthCandidateSubmitted && c?.spiritualHealthSpouseSubmitted)
                ? 'complete'
                : (c?.spiritualHealthCandidateSubmitted || c?.spiritualHealthSpouseSubmitted)
                  ? 'in_progress'
                  : 'incomplete',
          ministryTransitions: (c as any)?.ministryTransitionsCompleted ? 'complete' : 'incomplete',
        };
        const completedCount = (Object.values(stepState).filter((s) => s === 'complete') || []).length;
        const showLegacyPercent = typeof (c as any)?.portalProgressPercentage !== 'number';
        const legacyPercent = portalPercent(c?.onboardingStep, c?.onboardingCompleted);

        return {
          id: p.id,
          teamKey: p.teamKey || 'A',
          portalCandidateId: c?.id,
          sermonReferenceUrl,
          candidateName,
          candidateEmail,
          candidatePhone,
          spouseName,
          spouseEmail,
          spousePhone,
          sendingChurch: c?.sendingChurch,
          portalProgress,
          teamProgress,
          completedCount,
          legacyPercent,
          stepState,
          showLegacyPercent,
        };
      });
  }, [data?.data?.participants]);

  const visibleTeamKeys = useMemo(() => {
    const keys = new Set<'A' | 'B'>([
      ...(matrixQuery.data?.data?.teams || []),
      ...(data?.data?.participants || []).map((participant) => participant.teamKey || 'A'),
    ]);
    return (['A', 'B'] as const).filter((teamKey) => keys.has(teamKey));
  }, [data?.data?.participants, matrixQuery.data?.data?.teams]);

  useEffect(() => {
    if (data?.data?.isDoubleAssessment && visibleTeamKeys.length && !visibleTeamKeys.includes(activeTeamKey)) {
      setActiveTeamKey(visibleTeamKeys[0]);
    }
  }, [activeTeamKey, data?.data?.isDoubleAssessment, visibleTeamKeys]);

  const assignedAssessorRoleIds = useMemo(() => {
    const ids = new Set<number>();
    const eventRoles = (data?.data?.eventRoles || []) as any[];
    for (const er of eventRoles) {
      const assignedToUser = data?.data?.isDoubleAssessment
        ? (er?.teamAssignments || []).some((assignment: any) => assignment.assignedUserId === user?.id && assignment.teamKey === activeTeamKey)
        : er?.assignedUserId === user?.id;
      if (assignedToUser && er?.role?.roleType === 'assessor' && er?.role?.isInterviewArea) {
        ids.add(er.role.id);
      }
    }
    return ids;
  }, [activeTeamKey, data?.data?.eventRoles, data?.data?.isDoubleAssessment, user?.id]);

  const assistingAssessorRoleIds = useMemo(() => {
    const ids = new Set<number>();
    const eventRoles = (data?.data?.eventRoles || []) as any[];
    for (const er of eventRoles) {
      const assistingUser = data?.data?.isDoubleAssessment
        ? (er?.teamAssignments || []).some((assignment: any) => assignment.assistantUserId === user?.id && assignment.teamKey === activeTeamKey)
        : er?.assistantUserId === user?.id;
      if (assistingUser && er?.role?.roleType === 'assessor' && er?.role?.isInterviewArea) {
        ids.add(er.role.id);
      }
    }
    return ids;
  }, [activeTeamKey, data?.data?.eventRoles, data?.data?.isDoubleAssessment, user?.id]);

  const reportAssignments = reportAssignmentsQuery.data?.data || [];
  const reportAssignmentByKey = useMemo(() => {
    const map = new Map<string, any>();
    for (const assignment of reportAssignments) {
      if (!assignment.participantId || !assignment.roleId) continue;
      map.set(`${assignment.participantId}:${assignment.roleId}`, assignment);
    }
    return map;
  }, [reportAssignments]);

  const reportGroups = useMemo(() => {
    const grouped = new Map<number, { participant: any; assignments: any[] }>();
    for (const assignment of reportAssignments) {
      const participant = assignment.participant;
      if (!participant) continue;
      const current = grouped.get(participant.id) || { participant, assignments: [] };
      current.assignments.push(assignment);
      grouped.set(participant.id, current);
    }
    return Array.from(grouped.values()).map((group) => ({
      ...group,
      assignments: group.assignments
        .slice()
        .sort((a, b) => ((a.role?.sortOrder ?? 0) - (b.role?.sortOrder ?? 0)) || ((a.role?.displayName || '').localeCompare(b.role?.displayName || ''))),
    }));
  }, [reportAssignments]);

  return (
    <Layout>
      <div className="space-y-6">
        {isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
            Loading…
          </div>
        )}

        {error && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">
            Failed to load assessment.
          </div>
        )}

        {!isLoading && !error && data?.data && (
          <>
            <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{data.data.name}</h1>
                {data.data.isDoubleAssessment && (
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">Double assessment</span>
                )}
              </div>
              <p className="text-gray-600 mt-2">
                {new Date(data.data.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                {data.data.location ? ` • ${data.data.location}` : ''}
              </p>
              {data.data.description && (
                <p className="text-gray-700 mt-3 whitespace-pre-wrap">{data.data.description}</p>
              )}
            </div>

            {canConfirmAttendance && (
              <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
                {attendanceQuery.isLoading ? (
                  <div className="text-sm text-gray-600">Loading attendance status…</div>
                ) : (
                  (() => {
                    const existing = attendanceQuery.data?.data;
                    const isConfirmed = Boolean(existing);
                    const isAttending = existing?.isAttending ?? attendanceDraft.isAttending;
                    const title = isConfirmed
                      ? (isAttending ? 'Attendance Confirmed' : 'Not Attending')
                      : 'Confirm Attendance';
                    const badgeClass = isAttending
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-gray-100 text-gray-700 border-gray-200';

                    if (isConfirmed && !isEditingAttendance) {
                      return (
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${badgeClass}`}>
                                {isAttending ? '✓' : '–'}
                              </span>
                              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                            </div>
                            {isAttending ? (
                              <div className="text-sm text-gray-600">
                                {existing?.hotelNeeded ? 'Hotel needed' : 'No hotel'}
                                {existing?.hotelNeeded && (
                                  <>
                                    {' '}•{' '}
                                    {existing?.hotelSundayNight && existing?.hotelMondayNight
                                      ? 'Sun + Mon'
                                      : existing?.hotelSundayNight
                                        ? 'Sunday'
                                        : existing?.hotelMondayNight
                                          ? 'Monday'
                                          : 'Night TBD'}
                                  </>
                                )}
                                {' '}• {existing?.guestCount || 0} guest(s)
                                {existing?.dietaryRestrictions ? ` • ${existing.dietaryRestrictions}` : ''}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-600">
                                You are marked as not attending.
                              </div>
                            )}
                          </div>
                          <button
                            className="inline-flex items-center px-4 py-2 rounded-md bg-gray-100 text-gray-900 text-sm hover:bg-gray-200"
                            onClick={() => setIsEditingAttendance(true)}
                          >
                            Edit
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-3">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                          <p className="text-sm text-gray-600">
                            Let the admin team know if you plan to attend this assessment.
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={attendanceDraft.isAttending}
                              onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, isAttending: e.target.checked }))}
                            />
                            Attending
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={attendanceDraft.hotelNeeded}
                              onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, hotelNeeded: e.target.checked }))}
                              disabled={!attendanceDraft.isAttending}
                            />
                            Hotel needed
                          </label>
                          {attendanceDraft.hotelNeeded && (
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={attendanceDraft.hotelSundayNight}
                                  onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, hotelSundayNight: e.target.checked }))}
                                  disabled={!attendanceDraft.isAttending}
                                />
                                Sunday night
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={attendanceDraft.hotelMondayNight}
                                  onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, hotelMondayNight: e.target.checked }))}
                                  disabled={!attendanceDraft.isAttending}
                                />
                                Monday night
                              </label>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <input
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                            type="number"
                            min={0}
                            placeholder="Guest count"
                            value={attendanceDraft.guestCount}
                            onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, guestCount: Number(e.target.value) }))}
                            disabled={!attendanceDraft.isAttending}
                          />
                          <input
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
                            placeholder="Guest names (comma separated)"
                            value={attendanceDraft.guestNames}
                            onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, guestNames: e.target.value }))}
                            disabled={!attendanceDraft.isAttending}
                          />
                        </div>

                        <textarea
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
                          rows={2}
                          placeholder="Dietary restrictions"
                          value={attendanceDraft.dietaryRestrictions}
                          onChange={(e) => setAttendanceDraft((prev) => ({ ...prev, dietaryRestrictions: e.target.value }))}
                          disabled={!attendanceDraft.isAttending}
                        />

                        <div className="flex items-center gap-3">
                          <button
                            className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50"
                            disabled={attendanceMutation.isPending}
                            onClick={() => attendanceMutation.mutate({
                              isAttending: attendanceDraft.isAttending,
                              hotelNeeded: attendanceDraft.hotelNeeded,
                              hotelSundayNight: attendanceDraft.hotelSundayNight,
                              hotelMondayNight: attendanceDraft.hotelMondayNight,
                              guestCount: attendanceDraft.guestCount || 0,
                              guestNames: attendanceDraft.guestNames,
                              dietaryRestrictions: attendanceDraft.dietaryRestrictions,
                            })}
                          >
                            Save attendance
                          </button>
                          {isConfirmed && (
                            <button
                              className="inline-flex items-center px-4 py-2 rounded-md bg-gray-100 text-gray-900 text-sm hover:bg-gray-200"
                              onClick={() => setIsEditingAttendance(false)}
                              disabled={attendanceMutation.isPending}
                            >
                              Cancel
                            </button>
                          )}
                          {attendanceMutation.isError && (
                            <span className="text-sm text-red-700">Failed to save attendance.</span>
                          )}
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white shadow-sm rounded-xl">
              <div className="flex gap-6 px-4 sm:px-6 border-b border-gray-200 overflow-x-auto">
                {[
                  { key: 'candidates', label: 'Candidates' },
                  { key: 'day-schedule', label: 'Day Schedule' },
                  { key: 'schedule', label: 'Interview Matrix' },
                  { key: 'assignments', label: 'Assignments' },
                  ...(canSeeReports ? [{ key: 'reports', label: 'Reports' }] : []),
                ].map((t) => {
                  const isActive = activeTab === (t.key as any);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      className={[
                        'py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px',
                        isActive ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-900',
                      ].join(' ')}
                      onClick={() => setActiveTab(t.key as any)}
                    >
                      {t.label}
                    </button>
                  );
                })}
                {canSeeReports ? (
                  <Link
                    to={`/assessments/${assessmentId}/after-party`}
                    className="py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px border-transparent text-gray-600 hover:text-gray-900"
                  >
                    After Party
                  </Link>
                ) : null}
                {canSeeEventDetails ? (
                  <Link
                    to={`/assessments/${assessmentId}/event-details`}
                    className="py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px border-transparent text-gray-600 hover:text-gray-900"
                  >
                    Event Details
                  </Link>
                ) : null}
              </div>
            </div>

            {activeTab === 'candidates' && (
              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Portal Candidates</h2>
                    <p className="text-sm text-gray-600">
                      {portalCandidateDetails.length} candidate{portalCandidateDetails.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Candidate</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Spouse</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sending Church</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {portalCandidateDetails.map((candidate) => (
                        <tr key={candidate.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <PersonCell name={candidate.candidateName} email={candidate.candidateEmail} phone={candidate.candidatePhone} />
                              {data.data.isDoubleAssessment && (
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">Team {candidate.teamKey}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {candidate.spouseName || candidate.spouseEmail || candidate.spousePhone ? (
                              <PersonCell name={candidate.spouseName || 'Spouse'} email={candidate.spouseEmail} phone={candidate.spousePhone} />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <SendingChurchCell sendingChurch={candidate.sendingChurch} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="flex flex-col gap-2">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                                  <span className="font-semibold uppercase tracking-wide">Portal</span>
                                  <span className="font-medium text-gray-900 whitespace-nowrap">
                                    {candidate.portalProgress}% <span className="text-gray-500">({candidate.completedCount}/10)</span>
                                  </span>
                                </div>
                                <ProgressBar value={candidate.portalProgress} barClassName="bg-primary-600" />
                              </div>
                              <div className="flex items-center gap-2">
                                <StepStatusIcon step="profile" state={candidate.stepState.profile} title="Profile" />
                                <StepStatusIcon step="whyChurchPlanting" state={candidate.stepState.whyChurchPlanting} title="Why Church Planting" />
                                <StepStatusIcon step="references" state={candidate.stepState.references} title="References" />
                                <StepStatusIcon step="sendingChurch" state={candidate.stepState.sendingChurch} title="Sending Church" />
                                <StepStatusIcon step="background" state={candidate.stepState.background} title="Background Check" />
                                <StepStatusIcon step="prepareEnrich" state={candidate.stepState.prepareEnrich} title="Prepare/Enrich" />
                                <StepStatusIcon step="spouseInterview" state={candidate.stepState.spouseInterview} title="Spouse Interview" />
                                <StepStatusIcon step="theology" state={candidate.stepState.theology} title="Theology" />
                                <StepStatusIcon step="spiritualHealth" state={candidate.stepState.spiritualHealth} title="Spiritual Health" />
                                <StepStatusIcon step="ministryTransitions" state={candidate.stepState.ministryTransitions} title="Ministry Transitions" />
                              </div>
                              <div className="space-y-2 pt-2">
                                <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                                  <span className="font-semibold uppercase tracking-wide">Team</span>
                                  <span className="font-medium text-gray-900 whitespace-nowrap">{candidate.teamProgress}%</span>
                                </div>
                                <ProgressBar value={candidate.teamProgress} barClassName="bg-emerald-600" />
                              </div>
                              {candidate.showLegacyPercent ? (
                                <div className="text-xs text-gray-500">Legacy: {candidate.legacyPercent}%</div>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {(candidate.portalCandidateId || candidate.sermonReferenceUrl) ? (
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                {candidate.portalCandidateId ? (
                                  <Link
                                    to={`/assessments/${assessmentId}/candidates/${candidate.portalCandidateId}`}
                                    className="text-sm font-medium text-primary-700 hover:underline"
                                  >
                                    View details
                                  </Link>
                                ) : null}
                                {candidate.sermonReferenceUrl ? (
                                  <a
                                    href={candidate.sermonReferenceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm font-medium text-primary-700 hover:underline"
                                  >
                                    Sermon Link
                                  </a>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                      {portalCandidateDetails.length === 0 && (
                        <tr>
                          <td className="px-4 py-6 text-sm text-gray-600" colSpan={5}>
                            No participants yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="lg:hidden divide-y divide-gray-200">
                  {portalCandidateDetails.map((candidate) => (
                    <div key={candidate.id} className="p-4 space-y-4">
                      <div className="sm:grid sm:grid-cols-2 sm:gap-4">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Candidate</div>
                          <PersonCell name={candidate.candidateName} email={candidate.candidateEmail} phone={candidate.candidatePhone} />
                        </div>

                        <div className="mt-4 sm:mt-0">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Spouse</div>
                          {candidate.spouseName || candidate.spouseEmail || candidate.spousePhone ? (
                            <PersonCell name={candidate.spouseName || 'Spouse'} email={candidate.spouseEmail} phone={candidate.spousePhone} />
                          ) : (
                            <div className="text-sm text-gray-700">—</div>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sending Church</div>
                        <SendingChurchCell sendingChurch={candidate.sendingChurch} />
                      </div>

                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Progress</div>
                        <div className="flex flex-col gap-2">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                              <span className="font-semibold uppercase tracking-wide">Portal</span>
                              <span className="font-medium text-gray-900 whitespace-nowrap">
                                {candidate.portalProgress}% <span className="text-gray-500">({candidate.completedCount}/10)</span>
                              </span>
                            </div>
                            <ProgressBar value={candidate.portalProgress} barClassName="bg-primary-600" />
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <StepStatusIcon step="profile" state={candidate.stepState.profile} title="Profile" />
                            <StepStatusIcon step="whyChurchPlanting" state={candidate.stepState.whyChurchPlanting} title="Why Church Planting" />
                            <StepStatusIcon step="references" state={candidate.stepState.references} title="References" />
                            <StepStatusIcon step="sendingChurch" state={candidate.stepState.sendingChurch} title="Sending Church" />
                            <StepStatusIcon step="background" state={candidate.stepState.background} title="Background Check" />
                            <StepStatusIcon step="prepareEnrich" state={candidate.stepState.prepareEnrich} title="Prepare/Enrich" />
                            <StepStatusIcon step="spouseInterview" state={candidate.stepState.spouseInterview} title="Spouse Interview" />
                            <StepStatusIcon step="theology" state={candidate.stepState.theology} title="Theology" />
                            <StepStatusIcon step="spiritualHealth" state={candidate.stepState.spiritualHealth} title="Spiritual Health" />
                            <StepStatusIcon step="ministryTransitions" state={candidate.stepState.ministryTransitions} title="Ministry Transitions" />
                          </div>
                          <div className="space-y-2 pt-2">
                            <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
                              <span className="font-semibold uppercase tracking-wide">Team</span>
                              <span className="font-medium text-gray-900 whitespace-nowrap">{candidate.teamProgress}%</span>
                            </div>
                            <ProgressBar value={candidate.teamProgress} barClassName="bg-emerald-600" />
                          </div>
                          {candidate.showLegacyPercent ? (
                            <div className="text-xs text-gray-500">Legacy: {candidate.legacyPercent}%</div>
                          ) : null}
                        </div>
                      </div>
                      {(candidate.portalCandidateId || candidate.sermonReferenceUrl) ? (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Details</div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            {candidate.portalCandidateId ? (
                              <Link
                                to={`/assessments/${assessmentId}/candidates/${candidate.portalCandidateId}`}
                                className="text-sm font-medium text-primary-700 hover:underline"
                              >
                                View details
                              </Link>
                            ) : null}
                            {candidate.sermonReferenceUrl ? (
                              <a
                                href={candidate.sermonReferenceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-medium text-primary-700 hover:underline"
                              >
                                Sermon Link
                              </a>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}

                  {portalCandidateDetails.length === 0 && (
                    <div className="px-4 py-6 text-sm text-gray-600">No participants yet.</div>
                  )}
                </div>

                {/* Legend */}
                <div className="px-4 py-4 sm:px-6 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">Legend:</span> green = complete, yellow = in progress, gray = incomplete
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-700">
                    {stepLabels.map((s) => (
                      <div key={s.key} className="flex items-center gap-2">
                        <StepStatusIcon step={s.key} state="complete" title={s.label} />
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'day-schedule' && (
              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Day Schedule</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Whole-day agenda. Interview times are listed as slots — see the Interview Matrix for who interviews whom.
                  </p>
                </div>
                <div className="p-4 sm:p-6">
                  {masterScheduleQuery.isLoading ? (
                    <div className="text-gray-700">Loading day schedule…</div>
                  ) : masterScheduleQuery.isError ? (
                    <div className="text-red-700">Failed to load day schedule.</div>
                  ) : !(masterScheduleQuery.data?.data?.days || []).some((d) => d.items.length) ? (
                    <div className="text-sm text-gray-600">No day schedule published yet.</div>
                  ) : (
                    <div className="space-y-6">
                      {(masterScheduleQuery.data?.data?.days || []).map((day) => (
                        <div key={day.dayIndex} className="space-y-3">
                          <h3 className="text-sm font-semibold text-gray-900">
                            Day {day.dayIndex + 1}
                            {day.date ? <span className="ml-2 font-normal text-gray-500">{day.date}</span> : null}
                          </h3>
                          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
                            {day.items.map((item) => {
                              const isInterview = item.slotType === 'interview';
                              return (
                                <div
                                  key={`${day.dayIndex}-${item.representativeSlotId}`}
                                  className={[
                                    'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3',
                                    isInterview ? 'bg-primary-50/40' : 'bg-white',
                                  ].join(' ')}
                                >
                                  <div className="flex items-start gap-3 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                      {item.startTimeLabel || String(item.startTime).slice(0, 5)}
                                      {item.endTimeLabel ? (
                                        <span className="text-gray-500 font-normal"> – {item.endTimeLabel}</span>
                                      ) : null}
                                    </div>
                                    <div className="min-w-0">
                                      {isInterview ? (
                                        <button
                                          type="button"
                                          className="text-sm font-medium text-primary-700 hover:underline text-left"
                                          onClick={() => setActiveTab('schedule')}
                                        >
                                          {item.label}
                                        </button>
                                      ) : (
                                        <div className="text-sm font-medium text-gray-900">{item.label}</div>
                                      )}
                                      {item.location ? (
                                        <div className="text-xs text-gray-500">{item.location}</div>
                                      ) : null}
                                    </div>
                                  </div>
                                  <span className={[
                                    'inline-flex self-start rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                                    isInterview
                                      ? 'bg-primary-100 text-primary-800'
                                      : item.slotType === 'meal'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : item.slotType === 'break'
                                          ? 'bg-gray-100 text-gray-700'
                                          : 'bg-slate-100 text-slate-700',
                                  ].join(' ')}>
                                    {item.slotType}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'schedule' && (
              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Interview Matrix</h2>
                    <p className="text-sm text-gray-600 mt-1">View-only interview matrix.</p>
                  </div>
                  {data.data.isDoubleAssessment && visibleTeamKeys.length > 1 && (
                    <div className="flex gap-2">
                      {visibleTeamKeys.map((teamKey) => (
                        <button
                          key={teamKey}
                          type="button"
                          onClick={() => setActiveTeamKey(teamKey)}
                          className={`rounded-md px-3 py-2 text-sm font-semibold ${activeTeamKey === teamKey ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}
                        >
                          Team {teamKey}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  {matrixQuery.isLoading ? (
                    <div className="p-4 sm:p-6 text-gray-700">Loading schedule…</div>
                  ) : matrixQuery.isError ? (
                    <div className="p-4 sm:p-6 text-red-700">Failed to load interview schedule.</div>
                  ) : (
                    (() => {
                      const matrix = matrixQuery.data?.data;
                      const roles = (matrix?.roles || [])
                        .filter((r: any) => r.roleType === 'assessor' && r.isInterviewArea)
                        .slice()
                        .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  
                      const eventRoleByRoleId = new Map<number, any>();
                      for (const er of (data.data.eventRoles || []) as any[]) {
                        if (er?.roleId) eventRoleByRoleId.set(er.roleId, er);
                      }
  
                      const assessorNameForRole = (roleId: number) => {
                        const er = eventRoleByRoleId.get(roleId);
                        const teamAssignment = data.data.isDoubleAssessment
                          ? (er?.teamAssignments || []).find((assignment: any) => assignment.teamKey === activeTeamKey)
                          : null;
                        const u = data.data.isDoubleAssessment ? teamAssignment?.assignedUser : er?.assignedUser;
                        const n = u
                          ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                          : (data.data.isDoubleAssessment ? teamAssignment?.assignedName : er?.assignedName) || '';
                        return n || '';
                      };

                      const assistantNameForRole = (roleId: number) => {
                        const er = eventRoleByRoleId.get(roleId);
                        const teamAssignment = data.data.isDoubleAssessment
                          ? (er?.teamAssignments || []).find((assignment: any) => assignment.teamKey === activeTeamKey)
                          : null;
                        const u = data.data.isDoubleAssessment ? teamAssignment?.assistantUser : er?.assistantUser;
                        const n = u
                          ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                          : (data.data.isDoubleAssessment ? teamAssignment?.assistantName : er?.assistantName) || '';
                        return n || '';
                      };

                      const isHighlightedRole = (roleId: number) => assignedAssessorRoleIds.has(roleId);
                      const isAssistingRole = (roleId: number) => assistingAssessorRoleIds.has(roleId) && !assignedAssessorRoleIds.has(roleId);
  
                      const defaultScheduleRows: Array<{ time: string; label?: string; interviewPeriodIndex?: number; isDivider?: boolean }> = [
                        { time: '10:30 AM', interviewPeriodIndex: 1 },
                        { time: '11:20 AM', label: 'Break', isDivider: true },
                        { time: '11:30 AM', interviewPeriodIndex: 2 },
                        { time: '12:30 PM', label: 'Lunch', isDivider: true },
                        { time: '1:15 PM', interviewPeriodIndex: 3 },
                        { time: '2:05 PM', label: 'Break', isDivider: true },
                        { time: '2:15 PM', interviewPeriodIndex: 4 },
                        { time: '3:05 PM', label: 'Break', isDivider: true },
                        { time: '3:15 PM', interviewPeriodIndex: 5 },
                        { time: '4:05 PM', label: 'Break', isDivider: true },
                        { time: '4:15 PM', interviewPeriodIndex: 6 },
                      ];

                      const masterItems = (masterScheduleQuery.data?.data?.days || [])
                        .flatMap((day) => day.items)
                        .slice()
                        .sort((a, b) => {
                          if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
                          return String(a.startTime).localeCompare(String(b.startTime));
                        });
                      const hasMasterInterviewRows = masterItems.some((item) => item.slotType === 'interview' && item.interviewPeriodIndex);
                      const scheduleRows: Array<{ time: string; label?: string; interviewPeriodIndex?: number; isDivider?: boolean }> = hasMasterInterviewRows
                        ? masterItems.map((item) => (
                          item.slotType === 'interview'
                            ? {
                              time: item.startTimeLabel || String(item.startTime).slice(0, 5),
                              interviewPeriodIndex: item.interviewPeriodIndex || undefined,
                            }
                            : {
                              time: item.startTimeLabel || String(item.startTime).slice(0, 5),
                              label: item.label,
                              isDivider: true,
                            }
                        ))
                        : defaultScheduleRows;
  
                      const cellByPeriodRoleId = new Map<string, any>();
                      for (const c of (matrix?.cells || []).filter((cell: any) => (cell.teamKey || 'A') === activeTeamKey)) {
                        cellByPeriodRoleId.set(`${c.interviewPeriodIndex}:${c.roleId}`, c);
                      }
  
                      const renderCell = (row: any, role: any) => {
                        if (!row.interviewPeriodIndex) return <span className="text-gray-400">—</span>;
                        const cell = cellByPeriodRoleId.get(`${row.interviewPeriodIndex}:${role.id}`);
                        const pc = (cell?.participant as any)?.portalCandidate;
                        if (!pc) return <span className="text-gray-400">—</span>;
  
                        const participantId = (cell?.participant as any)?.id;
                        const reportAssignment = participantId ? reportAssignmentByKey.get(`${participantId}:${role.id}`) : null;
                        const reportHref = reportAssignment ? `/assessments/${assessmentId}/reports/${reportAssignment.id}` : null;
                        const profileHref = `/assessments/${assessmentId}/candidates/${pc?.id}`;
                        const headshotUrl = getImageUrl(pc?.headshotImagePath, { width: 96, quality: 70, format: 'webp' });

                        const candidateName = displayCandidateName(pc);
                        const spouseName = displaySpouseName(pc);
  
                        const label = role.interviewAudience === 'spouse_only'
                          ? spouseName
                          : role.interviewAudience === 'candidate_only'
                            ? candidateName
                            : `${candidateName}${spouseName ? ` & ${spouseName}` : ''}`;

                        return (
                          <div className="flex items-center gap-3">
                            <Avatar name={label} size="sm" imageUrl={headshotUrl} />
                            <div className="flex flex-col gap-1">
                              {reportHref ? (
                                <Link to={reportHref} className="text-sm text-primary-700 hover:underline">
                                  {label}
                                </Link>
                              ) : (
                                <span className="text-sm text-gray-900">{label}</span>
                              )}
                              <Link to={profileHref} className="text-xs font-medium text-gray-600 hover:underline">
                                View details
                              </Link>
                            </div>
                          </div>
                        );
                      };
  
                      const audienceLabel = (role: any) => {
                        if (role.interviewAudience === 'candidate_only') return 'Candidate only';
                        if (role.interviewAudience === 'spouse_only') return 'Spouse only';
                        return 'Couple';
                      };

                      return (
                        <>
                          <div className="hidden lg:block">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                    Schedule
                                  </th>
                                  {roles.map((r: any) => (
                                    <th
                                      key={r.id}
                                      className={[
                                        'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap',
                                        isHighlightedRole(r.id)
                                          ? 'bg-primary-50 text-primary-800'
                                          : isAssistingRole(r.id)
                                            ? 'bg-amber-50 text-amber-900'
                                            : 'text-gray-600',
                                      ].join(' ')}
                                    >
                                      <div className="flex flex-col">
                                        <span className="inline-flex items-center gap-1">
                                          {r.displayName || r.name}
                                          {isAssistingRole(r.id) ? (
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold normal-case text-amber-800">
                                              Assisting
                                            </span>
                                          ) : null}
                                        </span>
                                        <span className="text-[11px] font-normal text-gray-500 normal-case">
                                          {audienceLabel(r)}
                                        </span>
                                        <span className="text-[11px] font-normal text-gray-700 normal-case">
                                          {assessorNameForRole(r.id) || '—'}
                                        </span>
                                        {assistantNameForRole(r.id) ? (
                                          <span className="text-[11px] font-normal text-gray-500 normal-case">
                                            Asst: {assistantNameForRole(r.id)}
                                          </span>
                                        ) : null}
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {scheduleRows.map((row) => {
                                  if (row.isDivider) {
                                    return (
                                      <tr key={`${row.time}-${row.label}`} className="bg-gray-100">
                                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                          <span className="font-medium">{row.time}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 text-center font-medium" colSpan={roles.length}>
                                          {row.label}
                                        </td>
                                      </tr>
                                    );
                                  }
    
                                  return (
                                    <tr key={`${row.time}-${row.interviewPeriodIndex}`} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        <span className="font-medium">{row.time}</span>
                                      </td>
                                      {roles.map((r: any) => (
                                        <td
                                          key={r.id}
                                          className={[
                                            'px-4 py-3 text-sm text-gray-900',
                                            isHighlightedRole(r.id) ? 'bg-primary-50' : isAssistingRole(r.id) ? 'bg-amber-50' : '',
                                          ].join(' ')}
                                        >
                                          {renderCell(row, r)}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                                {roles.length === 0 && (
                                  <tr>
                                    <td className="px-4 py-6 text-sm text-gray-600" colSpan={1}>
                                      No interview-area roles configured for this assessment yet.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="lg:hidden divide-y divide-gray-200">
                            {scheduleRows.map((row) => {
                              if (row.isDivider) {
                                return (
                                  <div key={`${row.time}-${row.label}`} className="px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700">
                                    {row.time} • {row.label}
                                  </div>
                                );
                              }

                              return (
                                <div key={`${row.time}-${row.interviewPeriodIndex}`} className="p-4 space-y-4">
                                  <div className="text-sm font-semibold text-gray-900">{row.time}</div>
                                  {roles.length === 0 ? (
                                    <div className="text-sm text-gray-600">
                                      No interview-area roles configured for this assessment yet.
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      {roles.map((r: any) => (
                                        <div
                                          key={r.id}
                                          className={[
                                            'rounded-lg border p-3',
                                            isHighlightedRole(r.id)
                                              ? 'border-primary-300 bg-primary-50'
                                              : isAssistingRole(r.id)
                                                ? 'border-amber-300 bg-amber-50'
                                                : 'border-gray-200',
                                          ].join(' ')}
                                        >
                                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {r.displayName || r.name}
                                            {isAssistingRole(r.id) ? ' · Assisting' : ''}
                                          </div>
                                          <div className="text-xs text-gray-500">{audienceLabel(r)}</div>
                                          <div className="text-xs text-gray-500">
                                            Assessor: {assessorNameForRole(r.id) || '—'}
                                          </div>
                                          {assistantNameForRole(r.id) ? (
                                            <div className="text-xs text-gray-500">
                                              Assistant: {assistantNameForRole(r.id)}
                                            </div>
                                          ) : null}
                                          <div className="mt-2 text-sm text-gray-900">
                                            {renderCell(row, r)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()
                  )}
                </div>
              </div>
            )}

            {activeTab === 'assignments' && (
              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Interview Roles</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Areas and the assessor assigned for this assessment.
                  </p>
                </div>
                <div className="overflow-x-auto hidden lg:block">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role / Area</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {((data.data.eventRoles || []) as any[])
                        .slice()
                        .sort((a, b) => ((a.role?.sortOrder ?? 0) - (b.role?.sortOrder ?? 0)) || ((a.role?.displayName || '').localeCompare(b.role?.displayName || '')))
                        .map((er) => (
                          <tr key={er.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {er.role?.displayName || er.role?.name || '—'}
                              {er.role?.roleType ? (
                                <span className="ml-2 text-xs text-gray-500">({er.role.roleType})</span>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {data.data.isDoubleAssessment && er.role?.isInterviewArea ? (
                                <div className="space-y-2">
                                  {(er.teamAssignments || []).map((assignment: any) => (
                                    <div key={assignment.teamKey} className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="w-14 text-xs font-semibold text-gray-500">Team {assignment.teamKey}</span>
                                        <AssessorCell assessorName={assignment.assignedName} assessorUser={assignment.assignedUser} />
                                      </div>
                                      {(assignment.assistantUser || assignment.assistantName) ? (
                                        <div className="ml-14 text-xs text-gray-500">
                                          Assistant:{' '}
                                          {assignment.assistantUser
                                            ? `${assignment.assistantUser.firstName || ''} ${assignment.assistantUser.lastName || ''}`.trim()
                                            : assignment.assistantName}
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <AssessorCell assessorName={er.assignedName} assessorUser={er.assignedUser} />
                                  {(er.assistantUser || er.assistantName) ? (
                                    <div className="text-xs text-gray-500">
                                      Assistant:{' '}
                                      {er.assistantUser
                                        ? `${er.assistantUser.firstName || ''} ${er.assistantUser.lastName || ''}`.trim()
                                        : er.assistantName}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      {(data.data.eventRoles || []).length === 0 && (
                        <tr>
                          <td className="px-4 py-6 text-sm text-gray-600" colSpan={2}>
                            No assessment roles have been configured for this assessment yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="lg:hidden divide-y divide-gray-200">
                  {((data.data.eventRoles || []) as any[])
                    .slice()
                    .sort((a, b) => ((a.role?.sortOrder ?? 0) - (b.role?.sortOrder ?? 0)) || ((a.role?.displayName || '').localeCompare(b.role?.displayName || '')))
                    .map((er) => (
                      <div key={er.id} className="p-4 space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Role / Area</div>
                        <div className="text-sm font-medium text-gray-900">
                          {er.role?.displayName || er.role?.name || '—'}
                          {er.role?.roleType ? (
                            <span className="ml-2 text-xs text-gray-500">({er.role.roleType})</span>
                          ) : null}
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned To</div>
                        <div className="text-sm text-gray-700">
                          {data.data.isDoubleAssessment && er.role?.isInterviewArea ? (
                            <div className="space-y-2">
                              {(er.teamAssignments || []).map((assignment: any) => (
                                <div key={assignment.teamKey} className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="w-14 text-xs font-semibold text-gray-500">Team {assignment.teamKey}</span>
                                    <AssessorCell assessorName={assignment.assignedName} assessorUser={assignment.assignedUser} />
                                  </div>
                                  {(assignment.assistantUser || assignment.assistantName) ? (
                                    <div className="ml-14 text-xs text-gray-500">
                                      Assistant:{' '}
                                      {assignment.assistantUser
                                        ? `${assignment.assistantUser.firstName || ''} ${assignment.assistantUser.lastName || ''}`.trim()
                                        : assignment.assistantName}
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <AssessorCell assessorName={er.assignedName} assessorUser={er.assignedUser} />
                              {(er.assistantUser || er.assistantName) ? (
                                <div className="text-xs text-gray-500">
                                  Assistant:{' '}
                                  {er.assistantUser
                                    ? `${er.assistantUser.firstName || ''} ${er.assistantUser.lastName || ''}`.trim()
                                    : er.assistantName}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  {(data.data.eventRoles || []).length === 0 && (
                    <div className="px-4 py-6 text-sm text-gray-600">
                      No assessment roles have been configured for this assessment yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="bg-white shadow-lg rounded-xl overflow-hidden">
                <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Assessment reports by candidate and assessor role.
                  </p>
                </div>

                {reportAssignmentsQuery.isLoading ? (
                  <div className="p-4 sm:p-6 text-gray-700">Loading reports…</div>
                ) : reportGroups.length === 0 ? (
                  <div className="p-4 sm:p-6 text-sm text-gray-600">No report assignments available yet.</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {reportGroups.map((group) => {
                      const portalCandidate = group.participant?.portalCandidate;
                      const candidateName = displayCandidateName(portalCandidate);
                      const spouseName = displaySpouseName(portalCandidate);
                      const candidateLabel = spouseName ? `${candidateName} & ${spouseName}` : candidateName;

                      return (
                        <div key={group.participant.id} className="p-4 sm:p-6 space-y-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            {candidateLabel}
                            {data.data.isDoubleAssessment && (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Team {group.participant.teamKey || 'A'}</span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {group.assignments.map((assignment) => {
                              const roleLabel = assignment.role?.displayName || assignment.role?.name || 'Assessor Role';
                              const isPrimary = assignment.assessorId === user?.id;
                              const isAssistant = assignment.assistantId === user?.id;
                              const canEdit = isPrimary || isAssistant;
                              const reportStatus = assignment.latestReport?.status;
                              const linkLabel = isAssistant && !isPrimary
                                ? 'Co-write report'
                                : canEdit
                                  ? 'Write report'
                                  : 'View report';
                              const hasReportLink = Boolean(assignment.id);

                              return (
                                <div
                                  key={`${assignment.participantId}-${assignment.roleId}`}
                                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                                >
                                  <div className="text-sm text-gray-800">
                                    {roleLabel}
                                    {isAssistant && !isPrimary ? (
                                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                        Assisting
                                      </span>
                                    ) : null}
                                    {reportStatus ? <span className="text-gray-500"> • {reportStatus}</span> : null}
                                    {!assignment.assessorId ? <span className="text-gray-400"> • Unassigned</span> : null}
                                  </div>
                                  {hasReportLink ? (
                                    <Link
                                      to={`/assessments/${assessmentId}/reports/${assignment.id}`}
                                      className="text-sm font-medium text-primary-700 hover:underline"
                                    >
                                      {linkLabel}
                                    </Link>
                                  ) : (
                                    <span className="text-sm text-gray-400">No assessor assigned</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentDetail;
